import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Tile3DIconButton — kleine 3D icoon-knop voor per-tegel acties.
 * Hergebruikt dezelfde recipe (inset highlight + drop-shadow + top-highlight)
 * als BadgeStatus3D / Card3D / Button3D, maar in een neutrale grijze tint
 * zodat de knop secondary aanvoelt.
 *
 * - Visueel ~32px, touch-target ≥44px via padding/min-w-h utilities op mobiel.
 * - Hover: lift -1px, sterkere shadow.
 * - Active: ingedrukt, lichtere shadow.
 * - error=true → rode rand (retry-cue).
 */
export interface Tile3DIconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  error?: boolean;
}

export const Tile3DIconButton = React.forwardRef<
  HTMLButtonElement,
  Tile3DIconButtonProps
>(({ className, children, error, disabled, ...rest }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={cn(
        "relative inline-flex items-center justify-center rounded-full",
        // visuele grootte ~32px desktop, ~36px mobile, met touch-target wrapper-padding
        "h-9 w-9 md:h-8 md:w-8",
        "text-slate-700 bg-slate-100 hover:bg-slate-200",
        "ring-1 ring-slate-300/60",
        "shadow-[0_3px_6px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.6),inset_0_-1px_0_rgba(0,0,0,0.12)]",
        "transition-all duration-150",
        "hover:-translate-y-0.5 hover:shadow-[0_5px_10px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(255,255,255,0.65),inset_0_-1px_0_rgba(0,0,0,0.12)]",
        "active:translate-y-0.5 active:shadow-[0_2px_4px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.4)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0",
        error && "border-2 border-red-500/60 ring-red-300/50",
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-1 top-0.5 h-1/2 rounded-full bg-white/40 blur-[1px]"
      />
      <span className="relative flex items-center justify-center">{children}</span>
    </button>
  );
});
Tile3DIconButton.displayName = "Tile3DIconButton";

export default Tile3DIconButton;
